import { useTranslation } from 'react-i18next';

import {
  CLIMATE_RISK_FACT_TYPES,
  ClimateRiskRatingOrder,
  ClimateRiskRatingsByIndex,
  PlotReportFactLabelNameEnum,
  Scenario,
} from '@/api/rest/resources/types/fact';
import { DefaultWidgetNames, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { isNullish } from '@/utils/booleans';

export const useRiskAggregatedOverviewConfiguration = () => {
  const { t } = useTranslation();

  const facts = useOrganizationIndicatorReportingFact(Object.keys(CLIMATE_RISK_FACT_TYPES), null, {
    [PlotReportFactLabelNameEnum.scenario]: Scenario.baseline,
  });

  let risks = Object.keys(facts)
    .filter((factType) => {
      const risk = facts[`${factType}`];
      return !isNullish(risk?.value);
    })
    .map((factType) => facts[`${factType}`])
    .sort((a, b) => {
      const aRating = ClimateRiskRatingsByIndex[a?.value as number];
      const bRating = ClimateRiskRatingsByIndex[b?.value as number];
      return ClimateRiskRatingOrder[aRating as keyof typeof ClimateRiskRatingOrder] >
        ClimateRiskRatingOrder[bRating as keyof typeof ClimateRiskRatingOrder]
        ? -1
        : 1;
    });

  // Limit to 7 risks for the chart
  risks = risks.slice(0, 7);
  return {
    name: DefaultWidgetNames.default_climate_risk_aggregated_chart,
    type: WidgetType.o_card,
    visible: true,
    relatedData: {
      header: t('climateRisk.riskOverview'),
      headerTooltip: t('climateRisk.riskOverviewTooltip.body'),
      styles: {
        root: 'border-none w-full',
        header: 'border-none typography-h3 text-text-primary px-0',
        body: 'border border-neutral-black-12 rounded-2xl flex-col flex h-full',
      },
    },
    widgets: [
      {
        name: 'climate_risk_sector_chart',
        type: WidgetType.o_c_risk_sector_chart,
        visible: true,
        relatedData: {
          risks,
        },
      },
    ],
  } as WidgetSlotConfiguration;
};
